// utils/offlineUploadQueue.js — Offline photo upload queue.
//
// Talent at venues often lose signal mid-shift. When a photo upload
// fails with a NETWORK error (no response / 502 / 503 / 504 / 520),
// callers enqueue the file here (IndexedDB, survives reloads and app
// closes). The queue auto-flushes when the browser fires `online`,
// on an interval, and shortly after boot.
import api from './api';
import { toast } from 'sonner';

const DB_NAME = 'ievntur-offline-uploads';
const STORE = 'uploads';
const MAX_ATTEMPTS = 5;

let _flushing = false;

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) {
        req.result.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx(db, mode, fn) {
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const store = t.objectStore(STORE);
    const out = fn(store);
    t.oncomplete = () => resolve(out && out.result !== undefined ? out.result : undefined);
    t.onerror = () => reject(t.error);
  });
}

export function isNetworkError(err) {
  if (!err) return false;
  if (err.response) return [502, 503, 504, 520].includes(err.response.status);
  return !!err.request || err.code === 'ERR_NETWORK' || /network/i.test(err.message || '');
}

export async function enqueueUpload({ endpoint, file, fields = {}, description = 'photo' }) {
  const db = await openDb();
  const item = {
    id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    endpoint,
    file,
    filename: file.name || 'photo.jpg',
    fields,
    description,
    attempts: 0,
    createdAt: Date.now(),
  };
  await tx(db, 'readwrite', (s) => s.put(item));
  window.dispatchEvent(new CustomEvent('offline-queue-changed'));
  return item.id;
}

export async function getQueuedCount() {
  try {
    const db = await openDb();
    return (await tx(db, 'readonly', (s) => s.count())) || 0;
  } catch {
    return 0;
  }
}

export async function flushQueue() {
  if (_flushing || !navigator.onLine) return;
  if (!localStorage.getItem('token')) return; // needs an authed session
  _flushing = true;
  try {
    const db = await openDb();
    const items = (await tx(db, 'readonly', (s) => s.getAll())) || [];
    if (!items.length) return;
    let uploaded = 0;
    for (const item of items) {
      try {
        const fd = new FormData();
        fd.append('file', item.file, item.filename);
        Object.entries(item.fields || {}).forEach(([k, v]) => fd.append(k, v));
        await api.post(item.endpoint, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        await tx(db, 'readwrite', (s) => s.delete(item.id));
        uploaded += 1;
      } catch (err) {
        if (isNetworkError(err)) break; // still offline — retry later
        item.attempts = (item.attempts || 0) + 1;
        if (item.attempts >= MAX_ATTEMPTS) {
          await tx(db, 'readwrite', (s) => s.delete(item.id));
        } else {
          await tx(db, 'readwrite', (s) => s.put(item));
        }
      }
    }
    if (uploaded > 0) {
      toast.success(`${uploaded} queued photo${uploaded > 1 ? 's' : ''} uploaded`, {
        description: 'Photos saved while you were offline have been sent.',
      });
    }
    window.dispatchEvent(new CustomEvent('offline-queue-changed'));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('offlineUploadQueue flush failed', err);
  } finally {
    _flushing = false;
  }
}

export function initOfflineUploadQueue() {
  window.addEventListener('online', () => setTimeout(flushQueue, 1500));
  setInterval(flushQueue, 90 * 1000);
  setTimeout(flushQueue, 5000);
}
